import { AssetLeaderboardSortPreset } from './AssetLeaderboard.types';

export const ASSET_LEADERBOARD_PRICE_METRIC = 'ReferenceRateUSD';
export const ASSET_LEADERBOARD_MARKET_CAP_METRIC = 'CapMrktEstUSD';
export const ASSET_LEADERBOARD_VOLUME_METRIC = 'volume_reported_spot_usd_1d';

/**
 * AG Grid colId of the live Price column — the single column whose visibility gates the CoinMetrics
 * live-price subscription (`useAssetLeaderboardPriceEnrichment`) and against which the enrichment lookup key
 * is resolved (`useBlotterVisibleRowsEnrichment`). Matches the Price column's `field` in
 * `useAssetLeaderboardColumns` (a field-only column's colId is its field — see `getAgGridColId`).
 */
export const ASSET_LEADERBOARD_PRICE_COLUMN_ID = 'price';

/**
 * AG Grid colId of the plain 7d % column — a field-only column, so its colId is just its field (see
 * `getAgGridColId`). Used by the Gainers sort preset below.
 */
export const ASSET_LEADERBOARD_CHANGE_7D_COLUMN_ID = 'change7dPct';

/**
 * AG Grid colId of the "7d Price Change" sparkline column. It reads the same `change7dPct` field as
 * `ASSET_LEADERBOARD_CHANGE_7D_COLUMN_ID`'s plain column, so it needs an explicit `id` (which wins over
 * `field` in `getAgGridColId`) to get a distinct colId and coexist with it.
 */
export const ASSET_LEADERBOARD_PRICE_CHANGE_7D_COLUMN_ID = 'priceChange7d';

/**
 * Display increment for the 1H/24H/7D percent-change cells (2 dp). Shared between
 * `InlineFormattedNumber`'s `increment` prop and the cells' flatness check so the two can't drift
 * apart — both must agree on what counts as "displays as zero".
 */
export const ASSET_LEADERBOARD_PERCENT_DISPLAY_INCREMENT = '0.01';

/** Fetched together in one snapshot request. */
export const ASSET_LEADERBOARD_METRICS = [
  ASSET_LEADERBOARD_PRICE_METRIC,
  ASSET_LEADERBOARD_MARKET_CAP_METRIC,
  ASSET_LEADERBOARD_VOLUME_METRIC,
];

/**
 * Display label per required metric, for the "key doesn't have access to X" message
 * (`useAssetLeaderboardData`'s `detectUnentitledLeaderboardMetrics`) — CoinMetrics metric ids aren't
 * meant to be shown to a user.
 */
export const ASSET_LEADERBOARD_METRIC_LABELS: Record<string, string> = {
  [ASSET_LEADERBOARD_PRICE_METRIC]: 'Price',
  [ASSET_LEADERBOARD_MARKET_CAP_METRIC]: 'Market Cap',
  [ASSET_LEADERBOARD_VOLUME_METRIC]: 'Volume',
};

/**
 * Shared between the eligibility check (`useAssetLeaderboardUniverse`) and the metrics fetch
 * (`useAssetLeaderboardData`) — CoinMetrics rejects a `/timeseries/asset-metrics` request outright if any
 * requested asset doesn't support a requested metric at this exact frequency, so both must agree.
 */
export const ASSET_LEADERBOARD_FREQUENCY = '1d';

/**
 * How old a metric's catalog `max_time` may be (at `ASSET_LEADERBOARD_FREQUENCY`) before an asset is
 * considered stale rather than eligible. All three metrics share this one daily/beginning-of-interval
 * cadence, so a single window covers them: the newest value CoinMetrics can ever have "today" is
 * stamped yesterday, and it takes a few hours after UTC midnight for that to finish computing across
 * every asset — a 1-day-only window can read as stale mid-rollout, so 2 days tolerates that lag.
 */
export const ASSET_LEADERBOARD_FRESHNESS_WINDOW_DAYS = 2;

/**
 * Daily points requested per asset: today plus 7 days back. Lets both the 24h and 7d change be
 * computed from a single fetch instead of two separate queries (the comparison points are then picked
 * by timestamp, not array position — see the offset/tolerance constants below).
 */
export const ASSET_LEADERBOARD_LOOKBACK_DAYS = 8;

/**
 * Target look-back offsets for the daily 24H and 7D changes, in hours. The comparison price is the
 * daily point closest to `latest − offset`, not a fixed array index.
 */
export const ASSET_LEADERBOARD_24H_OFFSET_HOURS = 24;
export const ASSET_LEADERBOARD_7D_OFFSET_HOURS = 7 * 24;

/**
 * How far the daily comparison point may sit from its target offset before the change is treated as
 * unavailable. The snapshot returns the N most recent *available* points (`paging_from='end'`), so a
 * thin asset that skipped a day would leave `at(-2)`/`[0]` spanning a wider window — a 48h move
 * mislabeled "24H", say. Picking the comparison point by timestamp within this tolerance drops that
 * case to a blank change instead. 12h cleanly admits the midnight-stamped daily point at exactly the
 * target day while rejecting the next-nearest one a full day off (the same interval-validation spirit
 * as the 1H `ASSET_LEADERBOARD_1H_MAX_GAP_MINUTES` guard).
 */
export const ASSET_LEADERBOARD_DAILY_CHANGE_TOLERANCE_HOURS = 12;

/**
 * Hourly refresh cadence for the REST snapshot and the 1H change fetch — EOD metrics change at most
 * once a day and CoinMetrics publishes a new `1h` point only hourly, so nothing finer would change a
 * value. The live price updates independently over the websocket (`useAssetLeaderboardPriceEnrichment`).
 */
export const ASSET_LEADERBOARD_POLL_MS = 60 * 60 * 1000;

/**
 * The `1H` CHANGE window compares the latest hourly `ReferenceRateUSD` point to the one an hour
 * earlier, so it needs its own `frequency=1h` fetch — the daily (`1d`) snapshot has no intra-day
 * granularity, and market cap (`CapMrktEstUSD`) is 1d-only so this can't be folded into the daily
 * request. It's a separate, price-only pass over the subset of the universe that supports
 * `ReferenceRateUSD` at `1h` (see `cmAssetIdsWith1hPrice` in `useAssetLeaderboardUniverse`), because the
 * CHANGE column is sortable (Gainers) and so needs the 1H value for every asset, not just
 * visible rows. Polled on the same hourly cadence as the daily snapshot (`ASSET_LEADERBOARD_POLL_MS`) —
 * CoinMetrics publishes a new `1h` point once per hour, so the value only ever steps hourly anyway.
 */
export const ASSET_LEADERBOARD_1H_FREQUENCY = '1h';

/**
 * Hourly points requested per asset: the latest and the one an hour before it — exactly the pair the
 * 1H delta needs, and deliberately not a full hourly series. A genuine hourly sparkline would be ~12×
 * this payload universe-wide; the 1H window reuses the daily sparkline instead (in fact hides it — an
 * hourly move is invisible on a daily line; see `AssetLeaderboardChangeCell`).
 */
export const ASSET_LEADERBOARD_1H_LOOKBACK = 2;

/**
 * How recent the latest hourly point must be for a 1H change to be shown, in hours. Tighter than the
 * daily freshness window because an hourly metric that's several hours stale isn't a meaningful "last
 * hour" move — such an asset shows a blank 1H rather than an old delta. Applied at point level on the
 * actually-returned data (catalog eligibility for `1h` still uses the shared daily window, which only
 * needs to confirm the `1h` frequency exists and isn't ancient).
 */
export const ASSET_LEADERBOARD_1H_FRESHNESS_WINDOW_HOURS = 3;

/**
 * Maximum allowed gap between the two hourly points used for the 1H change, in minutes. With
 * `limit_per_asset=2` CoinMetrics returns the two newest points, but a thinly-traded asset that skipped
 * an hour would have those two points 2h+ apart — making a "1H" change silently span a longer window.
 * Requiring the pair to be within roughly one hour (plus tolerance for publish jitter) drops that case
 * to a blank 1H rather than mislabeling a multi-hour move as one hour.
 */
export const ASSET_LEADERBOARD_1H_MAX_GAP_MINUTES = 90;

/**
 * The Talos-known universe can run into the thousands, and CoinMetrics' own `assets: '*'` wildcard
 * pulls in its entire tracked catalog (far more than the assets we'll actually show), which is slower
 * than fetching only what we need. Batch size keeps each request's URL comfortably short regardless
 * of universe size; concurrency bounds how many batches run in parallel against the CoinMetrics API.
 */
export const ASSET_LEADERBOARD_BATCH_SIZE = 150;
export const ASSET_LEADERBOARD_BATCH_CONCURRENCY = 4;

/**
 * Real-time price stream (`/timeseries-stream/asset-metrics`), scoped to currently-visible rows only
 * — see `useAssetLeaderboardPriceEnrichment`. CoinMetrics' stream supports only `1s`/`200ms` for ReferenceRateUSD;
 * `1s` is plenty for a table cell and keeps message volume down.
 */
export const ASSET_LEADERBOARD_LIVE_PRICE_FREQUENCY = '1s';

/**
 * Settle delay before reconnecting the price stream to a new visible-row set. CoinMetrics has no
 * in-place subscription amend (unlike Talos's WS) — changing the asset list means closing and
 * reopening the connection, and CoinMetrics' own docs note occasional Cloudflare-related restarts
 * already. This is intentionally more conservative than Watchlist's ~100ms amend-in-place debounce,
 * trading a little responsiveness for connection stability. Needs real-world tuning.
 */
export const ASSET_LEADERBOARD_LIVE_PRICE_DEBOUNCE_MS = 750;

/**
 * Coalescing window for incoming price ticks before they're applied to state — matches Watchlist's
 * own live-price pipeline (`debounceTime(100)` in `useMultipleStreamsSubscription.ts`), which batches
 * rapid successive ticks into one grid update instead of diffing the full row set on every message.
 */
export const ASSET_LEADERBOARD_LIVE_PRICE_BATCH_MS = 100;

export const ASSET_LEADERBOARD_DEFAULT_SORT_PRESET: AssetLeaderboardSortPreset = AssetLeaderboardSortPreset.MarketCap;

export const ASSET_LEADERBOARD_SORT_PRESET_LABELS: Record<AssetLeaderboardSortPreset, string> = {
  [AssetLeaderboardSortPreset.Gainers]: 'Gainers',
  [AssetLeaderboardSortPreset.Volume]: 'Volume',
  [AssetLeaderboardSortPreset.MarketCap]: 'Market Cap',
};

/**
 * Maps each leaderboard preset to the grid column + direction it applies via `applyColumnState` —
 * the same effect as the user clicking that column's header, so the resulting sort flows through
 * the normal grid-state persistence path.
 */
export const ASSET_LEADERBOARD_SORT_PRESET_COLUMN: Record<
  AssetLeaderboardSortPreset,
  { colId: string; sort: 'asc' | 'desc' }
> = {
  // Gainers sorts the 7d % column.
  [AssetLeaderboardSortPreset.Gainers]: { colId: ASSET_LEADERBOARD_CHANGE_7D_COLUMN_ID, sort: 'desc' },
  [AssetLeaderboardSortPreset.Volume]: { colId: 'spotVolumeUsd', sort: 'desc' },
  [AssetLeaderboardSortPreset.MarketCap]: { colId: 'marketCapUsd', sort: 'desc' },
};
